import React, { useState, useEffect } from 'react';
import { dealerOrderCommissionsApi } from '@/services/api';
import { OrderCommission, CommissionSummary } from '@/types';
import { useAuth } from '@/context/AuthContext';

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  earned: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  voided: 'bg-red-100 text-red-800',
};

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  earned: 'Earned',
  paid: 'Paid',
  voided: 'Voided',
};

export function MyCommissionsPage() {
  const { user } = useAuth();
  const [commissions, setCommissions] = useState<OrderCommission[]>([]);
  const [summary, setSummary] = useState<CommissionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCommissions, setTotalCommissions] = useState(0);
  const [pageSize] = useState(20);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
  }, [statusFilter, page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page,
        limit: pageSize,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      // Use the my-commissions endpoint which filters by the logged-in user's salesRepId
      const [commissionsResponse, summaryData] = await Promise.all([
        dealerOrderCommissionsApi.getMyCommissions(params),
        dealerOrderCommissionsApi.getSummary({ salesRepId: user?.salesRepId }),
      ]);

      setCommissions(commissionsResponse?.commissions || []);
      setTotalPages(commissionsResponse?.pagination?.pages || 1);
      setTotalCommissions(commissionsResponse?.pagination?.total || 0);
      setSummary(summaryData || null);
    } catch (err: any) {
      setError(err.message || 'Failed to load commission data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Disclaimer Banner */}
      <div className="text-xs text-gray-500 bg-keder-dark/50 px-4 py-2 rounded border border-keder-gray/30">
        Commission tracking is a convenience tool. Keder Screens does not pay or verify commissions.
      </div>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">My Commissions</h1>
        <p className="text-gray-400 mt-1">Track your earned commissions</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card">
            <h3 className="text-sm font-medium text-gray-400 mb-1">This Month</h3>
            <p className="text-2xl font-bold text-white">{formatCurrency(summary.totalThisMonth)}</p>
          </div>
          <div className="card">
            <h3 className="text-sm font-medium text-gray-400 mb-1">Year to Date</h3>
            <p className="text-2xl font-bold text-white">{formatCurrency(summary.totalYTD)}</p>
          </div>
          <div className="card bg-yellow-900/10 border-yellow-800">
            <h3 className="text-sm font-medium text-yellow-400 mb-1">Pending</h3>
            <p className="text-2xl font-bold text-white">{formatCurrency(summary.pendingAmount)}</p>
            <p className="text-xs text-gray-400 mt-1">{summary.pendingCount} orders</p>
          </div>
          <div className="card bg-green-900/10 border-green-800">
            <h3 className="text-sm font-medium text-green-400 mb-1">Earned</h3>
            <p className="text-2xl font-bold text-white">{formatCurrency(summary.earnedAmount)}</p>
            <p className="text-xs text-gray-400 mt-1">{summary.earnedCount} orders</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-400">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="select-field py-2 px-3 text-sm"
            >
              <option value="all">All Statuses</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {statusFilter !== 'all' && (
            <button
              onClick={() => setStatusFilter('all')}
              className="text-sm text-keder-yellow hover:text-yellow-400 font-medium flex items-center gap-1"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear Filter
            </button>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="card bg-red-900/20 border-red-800">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Results Count */}
      {!loading && (
        <div className="text-sm text-gray-400">
          Showing {(commissions || []).length} of {totalCommissions} commissions
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-keder-gray border-t-keder-yellow"></div>
          </div>
        </div>
      ) : (commissions || []).length === 0 ? (
        /* Empty State */
        <div className="card text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-white">No commissions yet</h3>
          <p className="mt-1 text-sm text-gray-400">
            Commissions will appear here when orders you create include a Customer Sell Price.
          </p>
        </div>
      ) : (
        /* Commissions Table */
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-keder-gray">
              <thead className="bg-keder-gray/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Customer Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-keder-gray/50">
                {(commissions || []).map((commission) => (
                  <tr key={commission.id} className="hover:bg-keder-gray/20">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-keder-yellow font-medium">{commission.orderNumber}</div>
                        {commission.orderSidemark && (
                          <div className="text-xs text-gray-400">{commission.orderSidemark}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {formatDate(commission.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                      {formatCurrency(commission.customerSellPrice)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {commission.commissionRate}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-keder-yellow">
                      {formatCurrency(commission.commissionAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[commission.status] || 'bg-gray-600 text-white'}`}>
                        {statusLabels[commission.status] || commission.status}
                      </span>
                      {commission.status === 'paid' && commission.paidAt && (
                        <div className="text-xs text-gray-400 mt-1">
                          Paid {formatDate(commission.paidAt)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-400">
              Page {page} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 bg-keder-gray text-white rounded-lg hover:bg-keder-gray/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 bg-keder-gray text-white rounded-lg hover:bg-keder-gray/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyCommissionsPage;
